import { Select, Input, Spin, Table, Tooltip } from "antd";
import React, { useState, useEffect } from "react";
import "../Reporting.scss";
import previewIcon from "./../../../../Assets/svg/table-action-preview.svg";
import { httpService } from "../../../../services/httpService.service";
import SearchIcon from "./../../../../Assets/svg/searchIcon.svg";
import MessagePopUp from "../../../components/Messages/MessagePopUp";
import { formatDateAndTime } from "../../../../services/helpFunctions/formatDateAndTime";
import { useFilePreview } from "../../completed/ReportingList/Download";
import Preview from "./Preview";
import Edit from "./Edit";
import editIcon from "../../../../Assets/svg/table-action-edit.svg";
import { isAuthorized } from "../../../../services/authService";
import { RolesEnum } from "../../../../services/rolesEnum";
import FacilityTag from "../../../components/FacilityTag/FacilityTag";
import { REPORTING_DEFAULT_COLUMNS } from "../../../components/SiteVisitColumns/siteVisitColumnOrder";
import { useSiteVisitColumns } from "../../../components/SiteVisitColumns/useSiteVisitColumns";

function DataChecks() {
  const { previewFiles } = useFilePreview();
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageSize: 10,
    pageNumber: 1,
    total: 0,
  });

  const [siteVisitCards, setSiteVisitCards] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSiteVisit, setSelectedSiteVisit] = useState(null);
  const [selectedVisitId, setSelectedVisitId] = useState(null);
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);

  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 576);
  const [message, setMessage] = useState({
    trigger: false,
    type: "",
    icon: "",
    title: "",
    text: "",
  });

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > 576);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageNumber: 1,
    }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { Option } = Select;

  const fetchSiteVisits = () => {
    const projectId = localStorage.getItem("currentProjectID");

    const params = {
      ProjectId: projectId,
      Search: debouncedQuery,
      pageSize: pagination.pageSize,
      pageNumber: pagination.pageNumber,
    };

    const filteredParams = Object.keys(params).reduce((acc, key) => {
      if (params[key] !== undefined && params[key] !== null) {
        acc[key] = params[key];
      }
      return acc;
    }, {});

    const queryString = new URLSearchParams(filteredParams).toString();

    const apiUrl = `/api/Site/getAllSiteVisits?${queryString}`;
    setLoading(true);
    httpService
      .get(
        apiUrl,
        (res) => {
          setSiteVisitCards(res.data);
          setPagination({
            pageSize: res.metaData.pageSize,
            pageNumber: res.metaData.pageNumber,
            total: res.metaData.totalItemCount,
          });
        },
        (error) => {
          // console.log(error);
        }
      )
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchSiteVisits();
  }, [pagination.pageNumber, pagination.pageSize, debouncedQuery]);

  const handleTogglePreviewModal = (record) => {
    const siteVisitCardId = record?.key;
    setSelectedSiteVisit(siteVisitCardId);
    setShowPreviewModal(!showPreviewModal);
  };

  const handleToggleEditModal = (record) => {
    const siteVisitCardId = record?.key;
    const visitId = record?.visitId; // Extract visitId from record
    setSelectedSiteVisit(siteVisitCardId);
    setSelectedVisitId(visitId); // Store visitId in state
    setShowEditModal(true);
  };

  const columns = [
    {
      title: <span className="styledTitle">Site Visit Identifier</span>,
      sorter: (a, b) =>
        a.siteVisitIdentifier.localeCompare(b.siteVisitIdentifier),
      dataIndex: "siteVisitIdentifier",
      key: "siteVisitIdentifier",
      render: (text, record) => (
        <span className="styledDataIndex3">{text}</span>
      ),
    },
    {
      title: <span className="styledTitle">Site Visit Name</span>,
      sorter: (a, b) => a.name.localeCompare(b.name),
      dataIndex: "name",
      key: "name",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Facility</span>,
      dataIndex: "facility",
      key: "facility",
      ellipsis: true,
      width: 250,
      sorter: (a, b) =>
        (a.facility?.name || "").localeCompare(b.facility?.name || ""),
      render: (facility) => <FacilityTag facility={facility} />,
    },
    // {
    //   title: <span className="styledTitle">Actual Date of Visit</span>,
    //   sorter: (a, b) => a.actualDateOfVisit.localeCompare(b.actualDateOfVisit),
    //   dataIndex: "actualDateOfVisit",
    //   key: "actualDateOfVisit",
    //   render: (text) => (
    //     <span className="styledDataIndex3">{formatDateAndTime(text)}</span>
    //   ),
    // },
    {
      title: <span className="styledTitle">IP</span>,
      sorter: (a, b) => a.ip.localeCompare(b.ip),
      dataIndex: "ip",
      key: "ip",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Location</span>,
      sorter: (a, b) => a.location.localeCompare(b.location),
      dataIndex: "location",
      key: "location",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Cluster</span>,
      sorter: (a, b) => a.cluster.localeCompare(b.cluster),
      dataIndex: "cluster",
      key: "cluster",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Virtual</span>,
      sorter: (a, b) => a.virtual.localeCompare(b.virtual),
      dataIndex: "virtual",
      key: "virtual",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },

    {
      title: <span className="styledTitle"></span>,
      key: "action",
      fixed: isLargeScreen && siteVisitCards.length > 0 ? "right" : undefined,

      render: (text, record) => (
        <div className="actionDiv justify-content-evenly">
          {/* {tracked && ( */}
          <img
            src={previewIcon}
            onClick={() => handleTogglePreviewModal(record)}
            style={{ cursor: "pointer" }}
          ></img>
          {isAuthorized([RolesEnum.SystemAdmin, RolesEnum.DataAnalyst]) && (
            <img
              src={editIcon}
              onClick={() => handleToggleEditModal(record)}
              style={{ cursor: "pointer" }}
            />
          )}
        </div>
      ),
    },
  ];

  const formatData = (siteVisitCard) => {
    return {
      key: siteVisitCard?.id,
      siteVisitIdentifier: siteVisitCard?.identifier,
      name: siteVisitCard?.name || "N/A",
      actualDateOfVisit: siteVisitCard?.tracking?.actualDateOfVisit,
      ip: siteVisitCard?.ipPlan?.implementingPartner?.name,
      location: siteVisitCard?.locationDetails?.country,
      cluster: siteVisitCard?.cluster?.name,
      virtual: siteVisitCard?.isVirtual ? "Yes" : "No",
      resources: siteVisitCard?.siteVisitFiles.filter((item) =>
        [1, 3, 4, 5].includes(item.fileCategory)
      ),
      visitId: siteVisitCard?.visit.id,
      reportingYear: siteVisitCard?.tracking?.reportingYear,
      reportingMonth: siteVisitCard?.tracking?.reportingMonth,
      trainingDate: siteVisitCard?.tracking?.trainingDate,
      trainingScoreInfo: siteVisitCard?.tracking?.trainingScoreInfo,
      dateOfReport: siteVisitCard?.tracking?.dateOfReport,
      dateOfDebrief: siteVisitCard?.tracking?.dateOfDebrief,
      rejectionReason: siteVisitCard?.rejectionReason || null,
      percentageOfDataCollectionCompleted:
        siteVisitCard?.tracking?.percentageOfDataCollectionCompleted,
      fieldTeamReportingStatus:
        siteVisitCard?.tracking?.fieldTeamReportingStatus,
      trainingIsCompleted: siteVisitCard?.tracking?.trainingIsCompleted,
    };
  };

  const formattedData = siteVisitCards?.map(formatData);

  const {
    columns: visibleColumns,
    columnsBar,
    components: tableComponents,
  } = useSiteVisitColumns({
    tableId: "dataChecks",
    columns: columns,
    defaultKeys: REPORTING_DEFAULT_COLUMNS,
  });

  const handleEdit = () => {
    fetchSiteVisits();

    setShowEditModal(false);
  };

  return (
    <Spin spinning={loading}>
      <div className="reportingListDiv">
        <div className="flagsPage mb-4">
          <span className="tableViewHeaderText">Data Checks</span>
        </div>

        <div className="filtersWrapper">
          <div className="containerFilters">
            <Input
              onChange={handleSearch}
              placeholder="Search... "
              suffix={<img src={SearchIcon} alt="Search" />}
            />
          </div>
        </div>
        <div
          className="w-100 my-3 d-flex justify-content-start gap-2"
          style={{
            color: "var(--2, #AA1A5F)",
            fontFamily: "Poppins",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: "600",
            lineHeight: "24px",
          }}
        >
          {loading ? (
            <Spin className="me-2" size="small" spinning={loading} />
          ) : (
            `Total: ${pagination.total} visits`
          )}
        {columnsBar && <span className="ms-auto">{columnsBar}</span>}
        </div>
        <div className="d-flex flex-column">
          <Table
            components={tableComponents}
            loading={loading}
            rowClassName={(record) =>
              record?.rejectionReason ? "rejectedRow" : ""
            }
            dataSource={formattedData}
            columns={formattedData?.length > 0 ? visibleColumns : []}
            pagination={{
              ...pagination,
              onChange: (page, pageSize) => {
                setPagination({
                  ...pagination,
                  pageNumber: page,
                  pageSize: pageSize,
                });
              },
            }}
            className="tableTPM"
            scroll={{ x: "max-content" }}
          />
        </div>
        <Preview
          onClose={handleTogglePreviewModal}
          visible={showPreviewModal}
          siteVisitCardId={selectedSiteVisit}
          // visitId=
        />
        <Edit
          visible={showEditModal}
          onCancel={() => setShowEditModal(false)}
          onOk={handleEdit}
          siteVisitCardId={selectedSiteVisit}
          visitId={selectedVisitId}
          setMessage={setMessage}
        />
      </div>
      <MessagePopUp
        trigger={message.trigger}
        type={message.type}
        icon={message.icon}
        messageTitle={message.title}
        messageText={message.text}
      />
    </Spin>
  );
}

export default DataChecks;
